import React from 'react';
import usePageTranslation from '../../../shared/hooks/usePageTranslation';
import LegalPage from '../components/LegalPage';
import LegalSection from '../components/LegalSection';
import LegalContactBlock from '../components/LegalContactBlock';
import {
  REGLEMENT_SECTIONS,
  formatRecommendedSanction,
  type SanctionKind,
} from '../../../shared/sanctions/reglement';

/** Couleur du badge de sanction selon le type (base). */
const kindBadgeClass: Record<SanctionKind, string> = {
  mute: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
  ban_temp: 'bg-orange-500/15 text-orange-300 border-orange-500/40',
  ban_perm: 'bg-red-500/15 text-red-300 border-red-500/40',
};

const ReglementSanctions: React.FC = () => {
  const { t, loading, currentLang } = usePageTranslation('public', 'ReglementSanctions');
  const lang: 'fr' | 'en' = currentLang === 'en' ? 'en' : 'fr';

  if (loading) return null;

  return (
    <LegalPage
      title={t('title')}
      subtitle={t('subtitle')}
      lastUpdate={t('lastUpdate')}
      lastUpdateDate={t('lastUpdateDate')}
    >
      {/* Préambule (non numéroté : les numéros sont réservés aux sections du barème) */}
      <div className="mb-10">
        <h2 className="text-2xl font-bold text-white mb-4">{t('intro.title')}</h2>
        <p className="text-lg text-gray-300">{t('intro.description')}</p>
      </div>

      {/* Comment lire le barème + légende des types de sanction */}
      <div className="mb-10 rounded-xl border border-primary-500/40 bg-primary-500/10 p-5">
        <h3 className="text-lg font-semibold text-white mb-2">{t('howItWorks.title')}</h3>
        <p className="text-gray-300 mb-3">{t('howItWorks.description')}</p>
        <ul className="space-y-2 text-gray-300 mb-4">
          {['codes', 'multiCodes', 'escalation'].map((key) => (
            <li key={key} className="flex items-start">
              <span className="text-primary-400 font-bold mr-2">•</span>
              {t(`howItWorks.${key}`)}
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-2">
          <span className={`px-2.5 py-1 rounded-md border text-xs font-medium ${kindBadgeClass.mute}`}>
            {t('legend.mute')}
          </span>
          <span className={`px-2.5 py-1 rounded-md border text-xs font-medium ${kindBadgeClass.ban_temp}`}>
            {t('legend.banTemp')}
          </span>
          <span className={`px-2.5 py-1 rounded-md border text-xs font-medium ${kindBadgeClass.ban_perm}`}>
            {t('legend.banPerm')}
          </span>
        </div>
      </div>

      {/* Barème : une section numérotée par section du règlement (le numéro = 1er segment du code) */}
      {REGLEMENT_SECTIONS.map((section) => (
        <LegalSection
          key={section.number}
          number={section.number}
          title={lang === 'en' ? section.titleEn : section.titleFr}
        >
          <div className="rounded-xl border border-gray-700/60 overflow-hidden">
            <div className="hidden sm:flex items-center gap-3 px-4 py-2 bg-gray-800/60 text-xs font-semibold uppercase tracking-wide text-gray-400">
              <span className="w-14 shrink-0">{t('columns.code')}</span>
              <span className="flex-1">{t('columns.infraction')}</span>
              <span className="shrink-0">{t('columns.sanction')}</span>
            </div>
            {section.articles.map((article) => (
              <div
                key={article.code}
                className="flex flex-col gap-2 border-t border-gray-700/40 px-4 py-3 first:border-t-0 sm:flex-row sm:items-center sm:gap-3 sm:first:border-t"
              >
                <span className="w-fit shrink-0 rounded border border-primary-500/30 bg-primary-500/10 px-2 py-0.5 font-mono text-sm font-bold text-primary-300 sm:w-14 sm:text-center">
                  {article.code}
                </span>
                <span className="flex-1 text-gray-200">
                  {lang === 'en' ? article.labelEn : article.labelFr}
                </span>
                <span
                  className={`w-fit shrink-0 rounded-md border px-2.5 py-1 text-xs font-medium ${kindBadgeClass[article.sanction.base.kind]}`}
                >
                  {formatRecommendedSanction(article.sanction, lang)}
                </span>
              </div>
            ))}
          </div>
        </LegalSection>
      ))}

      {/* Disclaimer */}
      <p className="mb-10 text-sm italic text-gray-400">{t('disclaimer')}</p>

      {/* Signalement et contact (non numéroté) */}
      <div className="mb-2">
        <h2 className="text-2xl font-bold text-white mb-4">{t('contact.title')}</h2>
        <LegalContactBlock
          description={t('contact.description')}
          discord={t('contact.discord')}
          discordAction={t('contact.discordAction')}
          email={t('contact.email')}
          owner={t('contact.owner')}
        />
      </div>
    </LegalPage>
  );
};

export default ReglementSanctions;
